import { observer } from 'mobx-react-lite';
import React from 'react';
import { IFacility } from '@/types';
import Head from 'next/head';
import { getParkingAtPath, getUrlCodeOrSlug } from '@/utils/newparkingatroutes';

interface ITitles {
  lowPrice: string;
  metaTitle: string;
  description: string;
}

export function checkLowPrice(
  searchAlternate: IFacility[],
  landing: any,
): ITitles | null {
  const name = (landing?.properties?.display_name || landing?.properties?.airport_name)
    ?.replace(/\bParking\b/gi, "")
    .trim();
  const arrr = searchAlternate ? [...searchAlternate]
    // ?.filter((x) => x.date_sold_out == 0)
    ?.filter(x => !!(x.facility_selling_price || x.facility_solid_selling_price))
    ?.sort(
      (a, b) =>
        (a.facility_selling_price || a.facility_solid_selling_price) -
        (b.facility_selling_price || b.facility_solid_selling_price)
    ) : [];
  if (arrr?.length > 0) {
    let lowPrice = "";
    if (arrr[0].currency_name == "CAD") {
      lowPrice = (
        arrr[0].currency_facility_selling_price || arrr[0].facility_solid_selling_price
      ).toFixed(2) + " CAD";
    }
    else {
      lowPrice = (
        arrr[0].facility_selling_price || arrr[0].facility_solid_selling_price
      ).toFixed(2);
    }
    let metaTitle = `${landing.properties.airport_name} ${getAirportType(
      landing
    )}Parking (${landing.properties.airport_initials}) | From $${lowPrice}/Day`;
    if (landing?.extra?.extra_title) {
      metaTitle = landing?.extra?.extra_title.replaceAll("$LOW_PRICE", lowPrice);
    }
    let metaDescription = landing?.airport_display_type == 1 ? `Find discounted parking near ${name ?? name}, ${landing?.airport_city ?? landing?.airport_city}. Great locations and low rates available to book today.` : landing?.airport_display_type == 2 ? `Find a great deal on ${landing.properties.airport_name} cruise parking. Compare the best parking rates. Free shuttles, secure lots, easy cancellations. Book now & save!` : `Find cheap short and long-term parking rates near ${landing.properties.airport_name} (${landing.properties.airport_initials}). Our discount lots offer free shuttles, 24/7 security, and easy cancellation.`;
    if (landing?.extra?.extra_description) {
      metaDescription = landing?.extra?.extra_description.replaceAll("$LOW_PRICE", lowPrice);
    }

    return {
      lowPrice: `Cheap $ ${lowPrice} ${landing.properties.airport_name
        } Parking | Lowest Cost ${landing.properties.airport_initials
        } ${getAirportType(landing)}Parking | ${landing.properties.airport_initials
        } Parking & Shuttle`,
      metaTitle: metaTitle,
      description: metaDescription,
    };
  }
  return null;
}

function getAirportType(landing: any) {
  return landing.airport_display_type == 1
    ? 'City '
    : landing.airport_display_type == 2
      ? 'Cruise '
      : '';
}

function TitleHead({
  landing,
  searchAlternate,
  landingData,
  lowestPrice,
  priceCurrency,
  maxSellingPrice,
  backGroundImage,
}: {
  landing: any;
  searchAlternate: { result: IFacility[] };
  landingData?: any;
  lowestPrice?: { price: number, currency: string };
  priceCurrency?: string;
  maxSellingPrice?: number;
  backGroundImage?: string;
}) {
  const landingAirportInitials = landing?.properties?.airport_initials;
  const pageTitle = landing?.properties?.seo_title
    ? landing?.properties?.seo_title
    : `${landing?.properties?.airport_name ?? ''} (${landingAirportInitials ?? ''
    }) | On Air Parking Cheap Discount Rates`;
  const metaStaticBlurb = `Cheap ${landing?.properties?.airport_name ?? ''
    } Parking At ${landingAirportInitials ?? ''
    }. We know, the price looks to good to be true! Don't worry, it's true, and it's a great location. In fact you'll be parking at a professional parking facility. You'll be e-mailed where you're parking for $2.75 a day once you make the purchase on our site. Reserve today!`;
  const metaDescription = landing?.properties?.landing_seo_description
    ? landing?.properties?.landing_seo_description
    : metaStaticBlurb;
  const titles = checkLowPrice(searchAlternate?.result, landing);

  const name = (landing?.properties?.display_name || landing?.properties?.airport_name)
    ?.replace(/\bParking\b/gi, "")
    .trim();

  return (
    <>
      <Head>
        <title>{landing?.airport_display_type == 1
          ? `Cheap Parking Near ${name}, ${landing?.airport_city}`
          : landing?.airport_display_type == 2
            ? `Book ${landing?.properties?.display_name?.trim()} From $${(lowestPrice?.price?.toFixed(2) ?? landing?.properties?.price)}${lowestPrice?.currency == "CAD" ? " CAD" : ""} /day.`
            : titles?.metaTitle ?? pageTitle}</title>
        <meta
          property="og:title"
          content={landing?.airport_display_type == 1
            ? `Cheap Parking Near ${name}, ${landing?.airport_city}`
            : landing?.airport_display_type == 2
              ? `Book ${landing?.properties?.display_name?.trim()} From $${(lowestPrice?.price?.toFixed(2) ?? landing?.properties?.price)}${lowestPrice?.currency == "CAD" ? " CAD" : ""} /day.`
              : titles?.metaTitle ?? pageTitle}
          key="og:title"
        />
        <meta
          property="og:description"
          content={titles?.description ?? metaDescription}
          key="og:description"
        />
        <meta
          name="description"
          content={titles?.description ?? metaDescription}
          key="description"
        />
        {landing?.properties?.display_name && landing?.airport_frienldy_url && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(
                {
                  "@context": "https://schema.org",
                  "@graph": [
                    {
                      "@type": "Product",
                      "@id": `https://www.onairparking.com/parking/${landing?.airport_frienldy_url}#product`,
                      "name": landingData?.properties?.display_name ? landingData?.properties?.display_name : landing?.properties?.display_name,
                      "description": landing?.airport_display_type == 1 ? `Find discounted parking near ${name ?? name} ${landing?.airport_city ?? landing?.airport_city}. Great locations and low rates available to book today.` : landing?.airport_display_type == 2 ? `Looking for cheap ${landing?.properties?.airport_name} cruise parking? Compare the best parking rates near ${landing?.properties?.airport_initials}. Free shuttles, secure lots, easy cancellations. Book now & save!` : `Compare off-airport parking options near ${landing?.properties?.airport_name} (${landing?.properties?.airport_initials}). Free shuttles, secure lots, easy cancellations, and rates from $${(lowestPrice?.price?.toFixed(2) ?? landing?.properties?.price)}${lowestPrice?.currency == "CAD" ? " CAD" : ""} per day.`,
                      "category": landing?.airport_display_type == 1 ? "City Parking" : landing?.airport_display_type == 2 ? "Cruiseport parking" : "Airport parking",
                      "brand": {
                        "@type": "Brand",
                        "name": "On Air Parking"
                      },
                      "url": `https://www.onairparking.com/parking/${landing?.airport_frienldy_url}`,
                      "image": [
                        backGroundImage
                      ],
                      "aggregateRating": {
                        "@type": "AggregateRating",
                        "bestRating": (landing.bestRating),
                        "reviewCount": (landing.reviewCount),
                        "ratingValue": (landing.ratingValue),
                      },
                      "additionalProperty": [
                        ...(landing?.airport_display_type != 1
                          ? [
                            {
                              "@type": "PropertyValue",
                              "name": landing?.airport_display_type == 2
                                ? "Cruise Name"
                                : "Airport code",
                              "value": landing?.airport_display_type == 2
                                ? landing?.properties?.airport_name
                                : landing?.properties?.airport_initials
                            }
                          ]
                          : []),
                        {
                          "@type": "PropertyValue",
                          "name": "Parking type",
                          "value": landing?.airport_display_type == 1
                            ? "City Parking"
                            : landing?.airport_display_type == 2
                              ? "Cruiseport Parking"
                              : "Off-airport parking"
                        }
                      ],
                      "offers": {
                        "@type": "AggregateOffer",
                        "url": `https://www.onairparking.com/parking/${landing?.airport_frienldy_url}`,
                        "priceCurrency": priceCurrency,
                        "lowPrice": (lowestPrice?.price?.toFixed(2) ?? landing?.properties?.price),
                        "price": (lowestPrice?.price?.toFixed(2) ?? landing?.properties?.price),
                        "highPrice": maxSellingPrice?.toFixed(2),
                        "offerCount": searchAlternate?.result.length,
                        "availability": "https://schema.org/InStock"
                      }
                    },
                    {
                      "@type": "BreadcrumbList",
                      "@id": `https://www.onairparking.com/parking/${landing?.airport_frienldy_url}#breadcrumb`,
                      "itemListElement": [
                        {
                          "@type": "ListItem",
                          position: 1,
                          name: "Home",
                          item: "https://www.onairparking.com/"
                        },
                        {
                          "@type": "ListItem",
                          position: 2,
                          name: landing.properties.airport_name,
                          item: `https://www.onairparking.com/parking/${landing?.airport_frienldy_url}`
                        }
                      ]
                    },
                    {
                      "@type": "ItemList",
                      "@id": `https://www.onairparking.com/parking/${landing?.airport_frienldy_url}#itemlist`,
                      "name": `Parking lots near ${landing?.properties?.airport_name}`,
                      "numberOfItems": searchAlternate?.result.length,
                      "itemListElement": searchAlternate?.result?.map((facility, index) => ({
                        "@type": "ListItem",
                        "position": index + 1,
                        "url": `https://www.onairparking.com/${getParkingAtPath(
                          {
                            airport_initials: landing?.properties?.airport_initials,
                            airport_frienldy_url: landing?.airport_frienldy_url,
                          }
                        )}/${getUrlCodeOrSlug(facility)}`,
                        "name": facility.facility_lot
                      }))
                    }
                  ]
                }
              )
            }}
          />
        )}
      </Head>
    </>
  );
}

export default observer(TitleHead);
